import {
  AccessQuery,
  AccessType,
  DataRoomPermission,
  DealGroupType,
  DealRoleType,
  FileAccessInput,
  FolderAccessInput,
  useAccessQuery,
  useActiveDealAccountQuery,
} from "../../graphql/generated";
import { Option } from "../../components/tailwind/Dropdown";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useParams,
} from "react-router-dom";
import { InviteRequests } from "./InviteRequests";
import { DealAccountDetails } from "./DealAccount";
import { PermissionCheck } from "@/src/components/permissions/PermissionCheck";
import { Button } from "@/src/components/tailwind/Button";
import { Grid3x2Icon, ListIcon, ShieldUserIcon, UsersIcon } from "lucide-react";
import useGqlClient from "@/src/hooks/useGqlClient";
import { useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { Avatars } from "@/src/components/Avatars";
import { Tag } from "@/src/components/Tag";
import { DataRoomPermissions } from "./AccessList";
import { useState } from "react";
import { CreateGroupModal } from "./CreateGroupModal";
import { Group } from "./Group";
import { Toggle } from "@/src/components/Toggle";
import { toasts } from "@/src/components/toasts/toasts";
import { createContext, useContext } from "react";
import { AccessMatrix } from "./AccessMatrix";
import { useSelector } from "react-redux";
import { authSelectors } from "@/src/store/auth/selector";
import Loading from "@/src/components/Loading";

interface AccessContextType {
  selectedToggle: "list" | "matrix";
  setSelectedToggle: (value: "list" | "matrix") => void;
}

const AccessContext = createContext<AccessContextType | undefined>(undefined);

function useAccessContext() {
  const context = useContext(AccessContext);
  if (!context) {
    throw new Error("useAccessContext must be used within AccessProvider");
  }
  return context;
}

export function Access() {
  const { dealId } = useParams<{ dealId: string }>();
  const account = useSelector(authSelectors.account);

  const client = useGqlClient();
  const activeDealAccountQuery = useActiveDealAccountQuery(client, {
    id: dealId,
  });

  if (activeDealAccountQuery.error) {
    return (
      <div className="flex-1 flex flex-col">
        <p className="text-persian-990 font-semibold">Something went wrong</p>
      </div>
    );
  }

  if (activeDealAccountQuery.isLoading || !activeDealAccountQuery.data) {
    return (
      <div className="flex-1 flex flex-col">
        <Loading />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col">
      <div className="p-8 pt-0 lg:p-0 flex-1 flex flex-col ">
        <Switch>
          <Route path="/deal/:dealId/access" exact>
            <PermissionCheck
              input={{
                permissionKey: "access:has_access",
                roleType: DealRoleType.Internal,
              }}
            >
              <Home />
            </PermissionCheck>
            <PermissionCheck
              input={{
                permissionKey: "guest_access:has_access",
                roleType: DealRoleType.Guest,
              }}
            >
              {activeDealAccountQuery.data.deal.activeDealAccount.dealGroup ? (
                <Redirect
                  to={`/deal/${dealId}/access/group/${activeDealAccountQuery.data.deal.activeDealAccount.dealGroup.id}`}
                />
              ) : (
                <div>
                  <p className="text-persian-990 font-semibold">
                    Something went wrong
                  </p>
                </div>
              )}
            </PermissionCheck>
          </Route>

          <Route path={`/deal/:dealId/access/group/:dealGroupId`} exact>
            <div className={`flex-1 flex flex-col`}>
              <Group />
            </div>
          </Route>

          <Route path={`/deal/:dealId/access/account/:dealAccountId`} exact>
            <div className={`flex-1 flex flex-col`}>
              <DealAccountDetails />
            </div>
          </Route>

          <Route path={`/deal/:dealId/access`}></Route>
          {/* <Route path={`/deal/:dealId/access/guest/`}>
            <div
              className={`flex flex-col flex-1 min-h-full xl:flex-row  xl:gap-x-7`}
            >
              <PermissionCheck
                input={{
                  permissionKey: "access:has_access",
                  roleType: DealRoleType.Internal,
                }}
              >
                <GuestAccessManagment />
              </PermissionCheck>
            </div>
          </Route> */}
          <Route path={`/deal/:dealId/access/requests`}>
            <div className="flex-1">
              <InviteRequests />
            </div>
          </Route>
          {/* <Route path={`/deal/:dealId/access/guests`}>
            <PermissionCheck
              input={{
                permissionKey: "guest_access:has_access",
                roleType: DealRoleType.Guest,
              }}
            >
              <GuestAccessTab />
            </PermissionCheck>
          </Route> */}
        </Switch>
      </div>
    </div>
  );
}

function Home() {
  const client = useGqlClient();
  const { dealId } = useParams<{ dealId: string }>();
  const accessQuery = useAccessQuery(client, {
    dealId,
  });
  const [selectedToggle, setSelectedToggle] = useState<"list" | "matrix">(
    "list",
  );

  return (
    <AccessContext.Provider value={{ selectedToggle, setSelectedToggle }}>
      <div>
        <HomeHeader query={accessQuery} />
        <HomeContent query={accessQuery} />
      </div>
    </AccessContext.Provider>
  );
}

function HomeHeader(props: { query: UseQueryResult<AccessQuery, unknown> }) {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [openModal, setOpenModal] = useState<"create_group" | "">("");
  const { dealId } = useParams<{ dealId: string }>();
  const { selectedToggle, setSelectedToggle } = useAccessContext();
  return (
    <div className="border-y border-concrete-100 w-full flex items-center justify-between px-4 py-3">
      <div className="flex items-center">
        <Toggle
          value={selectedToggle}
          options={[
            {
              id: "list",
              icon: ListIcon,
              label: "Groups",
            },
            { id: "matrix", icon: Grid3x2Icon, label: "Matrix" },
          ]}
          size="sm"
          variant="secondary"
          onChange={(id) => {
            setSelectedToggle(id as "list" | "matrix");
          }}
        />
      </div>
      <div className="flex items-center gap-x-2">
        {/* <Button
          variant="secondary"
          size="s"
          text="Invite new user"
          icon={PlusIcon}
        /> */}
        <Button
          onClick={() => {
            setOpenModal("create_group");
          }}
          variant="positive"
          size="s"
          text="New group"
          icon={UsersIcon}
        />
      </div>
      <CreateGroupModal
        open={openModal === "create_group"}
        onClose={() => {
          setOpenModal("");
        }}
        onCreate={(id) => {
          setOpenModal("");
          queryClient.invalidateQueries({
            queryKey: ["Access", { dealId: dealId }],
          });
          toasts.success("New group created");
          history.push(`/deal/${dealId}/access/group/${id}`);
        }}
        dealId={dealId}
        type={DealGroupType.Guest}
        permissions={
          props.query.data ? props.query.data.deal.dataRoom.permissions : []
        }
      />
    </div>
  );
}

function HomeContent(props: { query: UseQueryResult<AccessQuery, unknown> }) {
  const { selectedToggle } = useAccessContext();
  const { dealId } = useParams<{ dealId: string }>();
  if (props.query.error) {
    return (
      <div className="h-[calc(100vh-220px)] flex items-center justify-center">
        <div>
          <p className="font-semibold text-md text-concrete-800">
            Something went wrong
          </p>
        </div>
      </div>
    );
  }

  if (props.query.isLoading || !props.query.data) {
    return (
      <div className="p-4 w-full">
        <div className="animate-pulse">
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex gap-x-4">
                <div className="h-12 bg-gray-200 rounded w-full"></div>
              </div>
            ))}
            {[...Array(6)].map((_, i) => (
              <div key={i} className="flex gap-x-4">
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-96"></div>
                <div className="h-4 bg-gray-200 rounded w-32"></div>
                <div className="h-4 bg-gray-200 rounded w-32"></div>
                <div className="h-4 bg-gray-200 rounded w-24"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {selectedToggle === "list" ? (
        <div className="p-4 space-y-2">
          {props.query.data.deal.ownerGroups.map((og) => {
            return <GroupCard group={og} isOwner={true} />;
          })}
          {props.query.data.deal.guestGroups.map((gg) => {
            return <GroupCard group={gg} isOwner={false} />;
          })}
        </div>
      ) : (
        <AccessMatrix
          dealId={dealId}
          dataRoomId={props.query.data.deal.dataRoom.id}
          accountSearch=""
        />
      )}
    </div>
  );
}

function GroupCard(props: {
  group: AccessQuery["deal"]["ownerGroups"][0];
  isOwner: boolean;
}) {
  const history = useHistory();
  const { dealId } = useParams<{ dealId: string }>();

  const { group } = props;

  return (
    <div
      onClick={() => {
        history.push(`/deal/${dealId}/access/group/${group.id}`);
      }}
    >
      <div className="flex items-center p-4 rounded-md border border-concrete-100 hover:bg-persian-50 hover:border-persian-100 cursor-pointer mb-2">
        <div className="flex items-center flex-1">
          <p className="font-semibold text-persian-990 text-md">{group.name}</p>

          <div className="ml-4 flex items-center gap-x-2">
            <Avatars
              size="s"
              accounts={group.accounts.map((da) => da.account)}
              max={6}
            />
            <p className="text-concrete-600 text-sm">
              {group.accounts.length} member
              {group.accounts.length > 1 ? "s" : ""}
            </p>
            {props.isOwner ? (
              <div className="relative group flex items-center gap-x-1.5 py-1 px-2 rounded-full border border-yellow-500 bg-yellow-100 hover:bg-yellow-200 hover:border-yellow-700 text-yellow-900">
                <ShieldUserIcon className="w-4 h-4" />
                <p className="text-sm font-semibold">Admin group</p>
                <div className="absolute left-1/2 top-full mt-2 z-10 hidden group-hover:block">
                  <div className="bg-concrete-950 z-50 text-white text-xs rounded px-2 py-1 shadow-md whitespace-nowrap">
                    All users in this group have full access to the Data Room
                    and Questions
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex items-center gap-x-2">
          {props.isOwner ? (
            <>
              <p className="font-semibold text-sm text-persian-990">
                Access to
              </p>
              <Tag color="blue" text="All documents" />
            </>
          ) : (
            <DataRoomPermissions
              restrictedAccess={group.overrideDataRoomAccess}
              granularAccess={group.granularAccess}
              permissions={group.dataRoomPermissions}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export interface GranularAccess {
  folders: {
    [key: string]: {
      accessType: AccessType;
      parentFolders: string[];
    };
  };

  files: {
    [key: string]: {
      accessType: AccessType;
      dataRoomPermission: Pick<
        DataRoomPermission,
        "id" | "name" | "description"
      >;
      folderId: string;
    };
  };
}

export function accessTypeToOption(accessType: AccessType): Option {
  switch (accessType) {
    case AccessType.NoAccess:
      return { label: "No access", value: AccessType.NoAccess };
    case AccessType.View:
      return { label: "View", value: AccessType.View };
    case AccessType.Download:
      return { label: "Download", value: AccessType.Download };
    case AccessType.Write:
      return { label: "Edit", value: AccessType.Write };
  }
}

export function granularAccessToFileInput(
  granularAccess: GranularAccess,
): Array<FileAccessInput> {
  // recurse through all child folders and append to array
  let files: FileAccessInput[] = [];

  Object.keys(granularAccess.files).forEach((fileId) => {
    files.push({
      fileId,
      type: granularAccess.files[fileId].accessType,
    });
  });

  return files;
}

export function granularAccessToFolderInput(
  granularAccess: GranularAccess,
): Array<FolderAccessInput> {
  let folders: FolderAccessInput[] = [];

  Object.keys(granularAccess.folders).forEach((folderId) => {
    folders.push({
      folderId,
      type: granularAccess.folders[folderId].accessType,
    });
  });

  return folders;
}
